/**
 * Small label + big value tile used on dashboards (Driver detail, top-line
 * Dashboard, etc.). Lives in `components/ui` so any new dashboard surface
 * can drop one in without redefining the styling.
 *
 * Originally lived inline in `Dashboard.tsx`; lifted here when the driver
 * detail page started to need them.
 */

import type { ReactNode } from "react";
import { Card } from "./Card";

type StatTone = "default" | "amber" | "red" | "green";

interface StatCardProps {
  label: string;
  value: ReactNode;
  /** Optional small line under the value — e.g. context like "best ever". */
  hint?: ReactNode;
  /** Visual tone for threshold-driven values (e.g. lapsed-driver tile). */
  tone?: StatTone;
  /** Dim the card to indicate the underlying query is stale. */
  stale?: boolean;
}

const TONE_STYLES: Record<StatTone, { card: string; value: string }> = {
  default: { card: "",                                  value: "text-gray-900" },
  amber:   { card: "bg-amber-50 border-amber-200",      value: "text-amber-700" },
  red:     { card: "bg-red-50 border-red-200",          value: "text-red-700" },
  green:   { card: "bg-green-50 border-green-200",      value: "text-green-700" },
};

export function StatCard({ label, value, hint, tone = "default", stale = false }: StatCardProps) {
  const t = TONE_STYLES[tone];
  const valueTitle = typeof value === "string" || typeof value === "number" ? String(value) : undefined;
  return (
    <Card className={`p-3 ${t.card} ${stale ? "opacity-50" : ""}`}>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p
        className={`text-lg font-semibold mt-0.5 truncate ${t.value}`}
        title={valueTitle}
      >
        {value}
      </p>
      {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      {stale && <p className="text-xs text-amber-500 mt-0.5">Stale</p>}
    </Card>
  );
}
